import React, { useMemo, useState } from "react";
import { FTPSearchBar } from "../../../components-new";

interface Props {
    data?: any[];
}

const barColors = [
    "#6366F1",
    "#F97316",
    "#10B981",
    "#EF4444",
    "#8B5CF6",
    "#14B8A6",
];

const BoothWiseResults = ({ data }: Props) => {
    const [search, setSearch] = useState("");

    if (!data || !Array.isArray(data)) {
        return <div className="p-6 text-sm text-gray-500">No booth data</div>;
    }

    const processed = useMemo(() => {
        return data.map((booth) => {
            const votesObj = booth.votes || {};

            const candidates = Object.entries(votesObj)
                .filter(
                    ([name]) =>
                        name !== "Total" &&
                        name !== "Total Valid Votes" &&
                        name !== "Votes for NOTA"
                )
                .map(([name, votes]) => ({
                    name,
                    votes: Number(votes),
                }))
                .sort((a, b) => b.votes - a.votes);

            return {
                booth: booth.polling_station_no,
                boothName: booth.polling_station_name,
                total: votesObj["Total"] || 0,
                validVotes: votesObj["Total Valid Votes"] || 0,
                nota: votesObj["Votes for NOTA"] || 0,
                candidates,
                winner: candidates[0],
                margin:
                    candidates.length >= 2
                        ? candidates[0].votes - candidates[1].votes
                        : 0,
            };
        });
    }, [data]);

    const filtered = processed.filter((b) => String(b.booth).includes(search));

    const summary = useMemo(() => {
        let totalVotes = 0;
        let totalNota = 0;

        const candidateTotals: Record<string, number> = {};

        processed.forEach((booth) => {
            totalVotes += booth.validVotes;
            totalNota += booth.nota;

            booth.candidates.forEach((c) => {
                candidateTotals[c.name] =
                    (candidateTotals[c.name] || 0) + c.votes;
            });
        });

        const leadingCandidate = Object.entries(candidateTotals).sort(
            (a, b) => b[1] - a[1]
        )[0];

        const strongBooths = [...processed]
            .sort((a, b) => b.margin - a.margin)
            .slice(0, 5);

        const closestBooths = [...processed]
            .sort((a, b) => a.margin - b.margin)
            .slice(0, 5);

        return {
            totalVotes,
            totalNota,
            leadingCandidate,
            strongBooths,
            closestBooths,
        };
    }, [processed]);

    return (
        <div className="space-y-6">
            {/* Search */}

            <div className="flex items-center justify-between">
                <FTPSearchBar
                    placeHolder="Search booth number..."
                    clientSideSearch
                    onSearch={(q) => setSearch(q)}
                />

                <div className="text-sm text-gray-500">
                    Showing {filtered.length} of {processed.length} booths
                </div>
            </div>

            {/* Leading Candidate */}

            {summary.leadingCandidate && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="text-sm text-gray-500">
                        Leading Candidate Across Booths
                    </div>

                    <div className="text-lg font-semibold text-blue-700">
                        {summary.leadingCandidate[0]}
                    </div>
                </div>
            )}

            {/* Booth Table */}

            <div className="overflow-auto rounded-xl border">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left">Booth</th>
                            <th className="px-4 py-3 text-left">
                                Polling Station
                            </th>
                            <th className="px-4 py-3 text-left">Leading</th>
                            <th className="px-4 py-3 text-left">
                                Top Candidates
                            </th>
                            <th className="px-4 py-3 text-left">Stats</th>
                        </tr>
                    </thead>

                    <tbody>
                        {filtered.map((booth) => (
                            <tr
                                key={booth.booth}
                                className="border-t hover:bg-gray-50"
                            >
                                <td className="px-4 py-3 text-lg font-semibold">
                                    {booth.booth}
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                    {booth.boothName}
                                </td>

                                <td className="px-4 py-3">
                                    <div className="font-medium text-green-600">
                                        {booth.winner?.name}
                                    </div>

                                    <div className="text-xs text-gray-500">
                                        {booth.winner?.votes} votes
                                    </div>
                                </td>

                                <td className="space-y-2 px-4 py-3">
                                    {booth.candidates
                                        .slice(0, 3)
                                        .map((c, i) => {
                                            if (c.votes < 10) return null;

                                            const maxVotes =
                                                booth.candidates[0]?.votes || 1;
                                            const percent = Math.max(
                                                5,
                                                (c.votes / maxVotes) * 100
                                            );

                                            const color =
                                                barColors[i % barColors.length];

                                            return (
                                                <div key={i}>
                                                    <div className="mb-1 flex justify-between text-xs">
                                                        <span>{c.name}</span>
                                                        <span>{c.votes}</span>
                                                    </div>

                                                    <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                                                        <div
                                                            className="h-full rounded transition-all duration-300"
                                                            style={{
                                                                width: `${percent}%`,
                                                                backgroundColor:
                                                                    color,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </td>

                                <td className="px-4 py-3 text-xs">
                                    <div>
                                        <b>Total:</b> {booth.total}
                                    </div>

                                    <div>
                                        <b>Valid Votes:</b> {booth.validVotes}
                                    </div>

                                    <div className="text-red-500">
                                        <b>NOTA:</b> {booth.nota}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BoothWiseResults;
